<template>
  <v-app class="app_wrapper no-scroll-bar" id="app">
    <div class="top_safe_area"></div>

    <v-main v-if="!loading" class="main_wrapper no-scroll-bar">
      <router-view />
    </v-main>

    <div
      v-else
      class="height100 d-flex justify-center align-center text-center"
    >
      <vue-loading
        type="bars"
        color="#EE6090"
        :size="{ width: '50px', height: '50px' }"
      />
    </div>

    <v-bottom-navigation
      class="bottom_nav"
      v-if="!loading && bottomNavActive"
      height="auto"
      grow
      color="primary"
      v-model="bottomNavValue"
      style="width: 100%" 
    >
      <template v-for="menu in bottomNavMenus" :key="menu.url">
        <v-btn
          @click="urlReplace(menu.url)"
          :disabled="menu.isDisabled"
          :value="menu.url"
        >
          <div
            v-if="menu.isImage"
            class="bottom_nav_icon nav-icon"
            :class="{ 'active-icon': String(bottomNavValue) === String(menu.url) }"
            :style="{ '--icon-url': `url(${menu.icon})` }"
          ></div>
          <v-icon v-else class="bottom_nav_icon">{{ menu.icon }}</v-icon>
          <span class="bottom_nav_label">{{ menu.title }}</span>
        </v-btn>
      </template>
    </v-bottom-navigation>
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import LocalDataService from '@/service/LocalDataServices';
// import { VueLoading } from 'vue-loading-template'; // 必要に応じてコメントアウト解除

// store
import { auth0Module } from '@/store/entityModules/auth0';
import { meModule } from '@/store/entityModules/me';
import { familyModule } from '@/store/entityModules/family';
import { pusherModule } from '@/store/entityModules/pusher';

// model
import { ErrorType } from '@/api/ApiClient';
import { t, getAppLocale } from '@/i18n';

// utils
import { isVevoEnabledNursery } from '@/utils/vevo';

type BottomNavItem = {
  titleKey: string;
  icon: string;
  url: string;
  isDisabled: boolean;
  isImage?: boolean;
};

const VEVO_NAV_ITEM: BottomNavItem = {
  titleKey: 'nav.bottomNav.vevo',
  icon: require('@/assets/vevo/nav_icon.png'),
  url: '/vevo',
  isDisabled: false,
  isImage: true,
};

export default defineComponent({
  name: 'App',

  components: {
    // VueLoading,
  },

  data: () => ({
    loading: false,
    bottomNavValue: '',
    bottomNavItems: [
      { titleKey: 'nav.bottomNav.home', icon: 'mdi-home', url: '/home', isDisabled: false },
      { titleKey: 'nav.bottomNav.record', icon: 'mdi-pencil', url: '/record', isDisabled: false },
      { titleKey: 'nav.bottomNav.calendar', icon: 'mdi-calendar', url: '/calendar', isDisabled: false },
      { titleKey: 'nav.bottomNav.settings', icon: 'mdi-cog', url: '/setting', isDisabled: false },
    ] as BottomNavItem[],
  }),

  computed: {
    bottomNavMenus(): Array<BottomNavItem & { title: string }> {
      const items = [...this.bottomNavItems];
      if (this.showVevoTab) {
        items.splice(items.length - 1, 0, VEVO_NAV_ITEM);
      }
      return items.map((item) => ({
        ...item,
        title: t(item.titleKey),
      }));
    },
    family() {
      return familyModule.family || null;
    },
    vevoChildren() {
      return (this.family?.children ?? []).filter((child) =>
        isVevoEnabledNursery(child.nurseryName),
      );
    },
    showVevoTab(): boolean {
      return true;
    },
    bottomNavActive() {
      if (
        this.routePath === '' ||
        this.routePath === '/' ||
        this.routePath === '/signin' ||
        this.routePath === '/callback' ||
        this.routePath === '/chat' ||
        // create-user 削除
        this.routePath === '/create-family' ||
        this.routePath === '/record/day' ||
        this.routePath === '/record/calendar' ||
        this.routePath === '/parent/edit' ||
        this.routePath === '/child/edit' ||
        this.routePath === '/family/edit' ||
        this.routePath === '/maintenance' ||
        this.routePath === '/verify/email' ||
        this.routePath === '/error/email/verify'
      ) {
        return false;
      } else {
        return true;
      }
    },
    routePath(): string {
      return this.$route.path;
    },
    meLanguage(): number | null | undefined {
      return (meModule.me as { language?: number | null } | null)?.language;
    },
  },

  watch: {
    routePath: {
      immediate: true,
      handler(newPath: string) {
        this.bottomNavValue = newPath?.startsWith('/vevo') ? '/vevo' : newPath;
      },
    },
    meLanguage: {
      immediate: true,
      handler() {
        this.syncVuetifyLocale();
      },
    },
  },

  async created() {
    this.loading = true;
    try {
      await auth0Module.initClient();
      const isAuthenticated = await auth0Module.isAuthenticated();
      
      if (isAuthenticated) {
        await this.isToFirstStep();
      } else {
        try {
          await auth0Module.getTokenSilently();
          await this.isToFirstStep();
        } catch (error: any) {
          const errorMsg = error instanceof Error ? error.message : error;

          // isToFirstStep の中で処理しきれなかった致命的なエラー（NO_TOKEN等）をキャッチ
          if (errorMsg === ErrorType.NO_TOKEN) {
            throw error; 
          }

          if (this.$route.query && this.$route.query.code && this.$route.query.state) {
            try {
              await auth0Module.handleAuth0();
            } catch (innerError) {
              window.location.reload();
            }
          } else if (this.$route.query.error && (this.$route.query.error === 'unauthorized' || this.$route.query.error === 'access_denied')) {
            this.$router.replace('/error/email/verify');
          } else {
            await auth0Module.signinProcess();
          }
        }
      }
    } catch (error: any) {
      const errorMsg = error instanceof Error ? error.message : error;
      if (errorMsg === ErrorType.NO_TOKEN) {
        this.loading = false;
        auth0Module.logout();
      } else {
        this.loading = false;
      }
    } finally {
      this.loading = false;
    }
  },

  async mounted() {
    window.receivedChatPush = (chatId: string) => {
      // do something
    };

    if (!familyModule.family && meModule.me) {
      await familyModule.fetchFamilyById({
        familyId: meModule.me.families[0].familyId,
      });
    }
  },

  methods: {
    syncVuetifyLocale() {
      const loc = getAppLocale();
      const vuetifyLocale = (this as any).$vuetify?.locale;
      if (!vuetifyLocale) return;
      if (typeof vuetifyLocale.change === 'function') {
        vuetifyLocale.change(loc);
      } else {
        vuetifyLocale.current = loc;
      }
    },
    urlReplace(path: string) {
      localStorage.removeItem("selectedDate");
      if (path === VEVO_NAV_ITEM.url && this.vevoChildren.length > 0) {
        const childId = String(this.vevoChildren[this.vevoChildren.length - 1].childId);
        this.$router.replace({ path, query: { childId } });
        return;
      }
      this.$router.replace(path);
    },
    async isCreateFamily() {
      if (meModule.me && meModule.me.families.length > 0) {
        const sessionsToken = await auth0Module.getAuth0Token();
        const parentId = String(meModule.me.parentId);
        LocalDataService.setParentId(parentId);
        LocalDataService.setBeamsTokenAndId(`${sessionsToken},${parentId}`);
        await Promise.all([
          familyModule.fetchFamilyById({
            familyId: meModule.me.families[0].familyId,
          }),
          pusherModule.subscribe(),
        ]);
        
        if (
          this.routePath === '' ||
          this.routePath === '/' ||
          this.routePath === '/signin' ||
          this.routePath === '/callback' ||
          // create-user の条件を削除
          this.routePath === '/create-family'
        ) {
          this.$router.replace('/home');
        }
      } else {
        this.$router.replace('/create-family');
      }
    },
    
    // FIRSTLOGINを検知したら、裏側で自動作成して次に進むロジックに完全書き換え
    async isToFirstStep() {
      try {
        await meModule.fetchMe();
        await this.isCreateFamily();
      } catch (error: any) {
        const errorMsg = error instanceof Error ? error.message : error;
        
        if (errorMsg === ErrorType.FIRSTLOGIN) {
          // ユーザーが存在しない場合、Auth0のプロフィール情報を使って裏で自動作成する
          const getAuth0User = await auth0Module.getAuth0User();
          if (getAuth0User) {
            await meModule.postMe({
              firstName: getAuth0User.family_name ? getAuth0User.family_name : '', // 必須項目の場合へのフォールバック
              lastName: getAuth0User.given_name ? getAuth0User.given_name : '',
              firstNameKana: null,
              lastNameKana: null,
              imageUrl: getAuth0User.picture ? getAuth0User.picture : null,
              birth: null,
              bloodType: null,
              tel: null,
            });
            // ユーザー作成に成功したら、そのまま家族作成フローへ進む
            await this.isCreateFamily();
          } else {
            // Auth0のユーザー情報すら取れなかった場合はログアウト扱いにするなど
            throw new Error(ErrorType.NO_TOKEN);
          }
        } else {
          // それ以外の予期せぬエラーは上に投げる
          throw error;
        }
      }
    },

    appUpdate() {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (const registration of registrations) {
            registration.update();
          }
        });
      }
    },
  },
});
</script>

<style lang="scss">
@use './sass/ress.scss';

/* --- 以下、既存のスタイルそのまま --- */
html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
}
.v-application {
  overflow-x: clip;
  background-color: #ffffff !important;
}
.app_wrapper {
  --bottom-nav-height: clamp(56px, 12vw, 72px);
  --bottom-nav-icon-size: clamp(25px, 5.5vw, 32px);
  --bottom-nav-label-size: clamp(14px, 3.2vw, 17px);
}
.main_wrapper {
  min-height: 100%;
  padding-bottom: calc(var(--bottom-nav-height) + env(safe-area-inset-bottom));
}
.height100 {
  height: 100%;
}
.no-scroll-bar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.no-scroll-bar::-webkit-scrollbar {
  display: none;
}
.bottom_nav {
  position: fixed !important;
  bottom: env(safe-area-inset-bottom) !important;
  left: 0 !important;
  right: 0 !important;
  width: 100vw !important;
  max-width: 100vw !important;
  margin: 0 !important;
  padding: 0 !important;
  z-index: 100 !important;
  overflow: hidden !important;

  .bottom_nav_icon {
    font-size: var(--bottom-nav-icon-size) !important;
    width: var(--bottom-nav-icon-size) !important;
    height: var(--bottom-nav-icon-size) !important;
  }

  .bottom_nav_label {
    white-space: nowrap;
    font-size: var(--bottom-nav-label-size) !important;
    letter-spacing: -0.5px;
    line-height: 1.2;
  }

  .v-btn {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    max-width: none !important;
    height: var(--bottom-nav-height) !important;
    min-height: var(--bottom-nav-height) !important;
    padding: 0 4px !important;
    color: #757575 !important;
    &.v-btn--selected {
      background-color: transparent !important;
      color: #EE6090 !important;

      .v-btn__content {
        color: #EE6090 !important;
      }
      .v-btn__overlay {
        background-color: transparent;
      }
    }
  }
}
.nav-icon {
  background-color: #757575;
  mask-image: var(--icon-url);
  mask-size: contain;
  mask-repeat: no-repeat;
  mask-position: center;
  -webkit-mask-image: var(--icon-url);
  -webkit-mask-size: contain;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
}
.active-icon {
  background-color: #EE6090;
}
#app {
  -webkit-tap-highlight-color: transparent;
}
:root {
  --app-top-offset: calc(env(safe-area-inset-top) + 0px) !important;
}
.top_safe_area {
  background-color: primary !important;
  height: var(--app-top-offset) !important;
  width: 100% !important;
  position: relative !important;
  z-index: 10 !important;
}
.v-bottom-navigation {
  z-index: 100 !important;
  position: fixed !important;
  bottom: 0 !important;
  width: 100% !important;
  max-width: 100% !important;
}
.v-bottom-navigation.bottom_nav {
  position: fixed !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  padding-bottom: env(safe-area-inset-bottom) !important;
  z-index: 100 !important; 
  background-color: white !important;
  height: calc(var(--bottom-nav-height) + env(safe-area-inset-bottom)) !important;
}
.v-overlay-scroll-blocked {
  padding-right: 0 !important;
}
</style>